import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'store';

import { PatientSelectionState } from './patientSelection.types';

const initialPatientSelectionState: PatientSelectionState = { selectedIds: [] };

const patientSelectionSlice = createSlice({
  initialState: initialPatientSelectionState,
  name: 'patientSelection',
  reducers: {
    // Add one or many ids, de-duplicated. Used by the row checkbox ("toggle on"),
    // "select page", "select all matching" and "select first N".
    addPatientSelection: (state, action: PayloadAction<string | string[]>) => {
      const incoming = Array.isArray(action.payload) ? action.payload : [action.payload];
      const merged = new Set([...state.selectedIds, ...incoming]);
      state.selectedIds = Array.from(merged);
    },
    clearPatientSelection: (state) => {
      state.selectedIds = [];
    },
    // Remove one or many ids. Used by the row checkbox ("toggle off").
    removePatientSelection: (state, action: PayloadAction<string | string[]>) => {
      const removing = new Set(Array.isArray(action.payload) ? action.payload : [action.payload]);
      state.selectedIds = state.selectedIds.filter((id) => !removing.has(id));
    },
    togglePatientSelection: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.selectedIds = state.selectedIds.includes(id)
        ? state.selectedIds.filter((item) => item !== id)
        : [...state.selectedIds, id];
    }
  }
});

export const selectPatientSelection = (state: RootState) => state.patientSelection;

// Memoized Set of selected ids for O(1) membership checks. Built once per
// selectedIds change and shared across every row checkbox, so per-row lookups
// don't re-scan the (potentially large) selectedIds array.
export const selectPatientSelectionIdSet = createSelector(
  selectPatientSelection,
  ({ selectedIds }) => new Set(selectedIds)
);

export const {
  addPatientSelection,
  clearPatientSelection,
  removePatientSelection,
  togglePatientSelection
} = patientSelectionSlice.actions;

export default patientSelectionSlice.reducer;
